import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { StaffService } from "@services";
import utils from "@utils";
import { ReassignJobsModal } from "./component";
import { ScrollDetect, SearchField, AddContactModal, ConfirmationModal } from "../../components";

import "./Staff.scss";

class Staff extends Component {
	constructor(props) {
		super(props);
		this.state = {
			pageNumber: 1,
			searchTerm: "",
			hasMore: true,
			staffMember: {},
			staff: [],
			showAddContactModal: false,
			editMode: false,
			showReassignJobsModal: false,
			staffId: undefined,
		};

		this._confirmationModal = createRef();
	}

	componentDidMount() {
		const {
			user: { IsStaffAdmin },
		} = this.props;

		/** Block page for non admin users */
		if (!IsStaffAdmin) {
			window.location.href = "/";
		}
	}

	/**
	 * closes the Reassign Modal and updates the state value of staff with data, unless the contact being deleted was not reassigned from the job.
	 * @param {Object} data Is an object which contains an array full of staffMembers.
	 */
	closeReassignJobsModal = ({ data } = {}) => {
		this.setState(prevState => ({
			showReassignJobsModal: false,
			staff: data || prevState.staff,
		}));
	};

	/** Gets the list of staffMembers displayed in the component */
	getStaff = () => {
		const { searchTerm, pageNumber } = this.state;
		const { user } = this.props;
		const { lang } = global;

		StaffService.getStaff({
			organizationId: user.Organization.Id,
			search: searchTerm === "" ? undefined : searchTerm,
			pageNumber,
		})
			.then(({ data }) => {
				const newStaff = (data.ItemList || []).map(staff => ({
					...staff,
					resettingPassword: false,
					deleting: false,
				}));
				this.setState(prevState => ({
					pageNumber: prevState.pageNumber + 1,
					hasMore: newStaff.length >= 25,
					staff: prevState.pageNumber > 1 ? [...prevState.staff, ...newStaff] : newStaff,
				}));
			})
			.catch(() => {
				this.setState({ staff: [], pageNumber: 1, hasMore: false });
				utils.notify({
					type: "error",
					message: lang.t("Error loading staff members"),
					timeOut: 1500,
				});
			});
	};

	/**
	 * Opens the delete conformation modal and allows the user to delete a staff Member using its staffMember Id.
	 * @param {Number} id Is a unique staffMember Id, used to delete the chosen staffMember.
	 */
	openDeleteConfirmationModal = id => () => {
		const { lang } = global;
		this._confirmationModal.current.openModal({
			title: lang.t("Delete Staff Member"),
			message: `${lang.t("Are you sure you want to delete this staff member_")} ${lang.t("This action cannot be undone_")}`,
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Staff Member")}`,
			onConfirm: () => this.deleteStaffMember(id),
		});
	};

	/**
	 * Deletes the staffMember from the list using the staffMember Id and then repopulates the staff list with all members except the deleted member.
	 * @param {Number} id Is a unique staffMember Id, used to delete the chosen staffMember by making the network request.
	 */
	deleteStaffMember = id => {
		const { lang } = global;
		this.setState(prevState => ({
			staff: prevState.staff.map(member =>
				member.Id === id ? { ...member, deleting: true } : member,
			),
		}));
		/** Delete staff on server and retrieve an updated list of staff members */
		StaffService.deleteStaff(id)
			.then(() => {
				this.setState(prevState => ({ staff: prevState.staff.filter(s => s.Id !== id) }));
				utils.notify({
					type: "success",
					message: lang.t("Staff member successfully deleted"),
					timeOut: 2000,
				});
			})
			//* * showing a Reassign modal if the staff member has existing job associations. */
			.catch(({ response: { status } }) => {
				if (status === 409) {
					this.setState({
						showReassignJobsModal: true,
						staffId: id,
					});
				}
			});
	};

	/**
	 * Opens the addContactModal and lets the user edit the selected staffMember by providing its staffMember Id.
	 * @param {Number} id Is a unique staffMember Id used to find the specific member within the staff list.
	 */
	editStaff = id => () => {
		const { staff } = this.state;
		const staffMember = staff.find(member => member.Id === id);

		if (staffMember) {
			this.setState({
				staffMember,
				showAddContactModal: true,
				editMode: true,
			});
		}
	};

	/**
	 * Opens the AddContactModal when a user clicks anywhere between the div on the web.
	 * @param {Number} memberId The Unique memberId which is provided to editStaff() when we click anywhere between the div.
	 * @param {Event} event HTML event to set preventNavigation value to decide if editStaff should be called.
	 */
	onRowClick = memberId => event => {
		const preventNavigation = event?.target?.dataset?.preventNavigation;
		if (!preventNavigation) this.editStaff(memberId)();
	};

	/**
	 * Opens the AddContactModal when a user clicks anywhere between the div on mobile by pressing.
	 * @param {Number} memberId The Unique memberId which is provided to editStaff() when we click anywhere between the div.
	 * @params {Event} event HTML event to set preventNavigation value to decide if editStaff should be called.
	 */
	onRowKeyPress = memberId => event => {
		const preventNavigation = event?.target?.dataset?.preventNavigation;
		if (!preventNavigation) this.onEditInvoice(memberId)();
	};

	/** Opens addContactModal so the user can create a staffMember */
	addNewStaff = () => {
		this.setState({
			showAddContactModal: true,
		});
	};

	/**
	 * Recently created staffMember is saved within the local state to show the user updated list.
	 * @param {Object} data An Object that contains an array which includes the updated staffMember list.
	 */
	onSaveNewStaff = ({ data }) => {
		const { staff } = this.state;
		const updatedIndex = staff.findIndex(member => member.Id === data.Id);
		if (updatedIndex !== -1) {
			staff[updatedIndex] = data;
			this.setState({ staff });
		} else {
			this.setState({ pageNumber: 1 }, this.getStaff);
		}
	};

	closeAddContactModal = () => {
		this.setState({
			showAddContactModal: false,
			editMode: false,
			staffMember: {},
		});
	};

	/**
	 * Searches for the typed name value on the SearchField
	 * @param {String} value Name that the user is searching for using the SearchField.
	 */
	handleStaffSearch = value =>
		this.setState({
			searchTerm: value,
			pageNumber: 1,
			staff: [],
			hasMore: true,
		});

	render() {
		const { lang } = global;
		const {
			user,
			user: { IsStaffAdmin, ContactId },
		} = this.props;
		const {
			staff,
			hasMore,
			showAddContactModal,
			staffMember,
			editMode,
			showReassignJobsModal,
			staffId,
		} = this.state;
		if (!IsStaffAdmin) {
			return null;
		}
		return (
			<div
				id="staff-list-container"
				className="container"
			>
				<h2>{lang.t("Staff")}</h2>
				<div
					id="staff-search-add"
					className="row"
				>
					<div className="col-xs-7 col-sm-4 col-md-3">
						<SearchField
							placeholder={global.lang.t("Search by Name")}
							onSearch={this.handleStaffSearch}
							clearable
						/>
					</div>
					<div className="col-xs-4 col-xs-offset-1 col-sm-2 col-sm-offset-6 col-md-2 col-md-offset-7 text-right">
						<button
							type="button"
							id="add-staff-member"
							className="btn btn-primary btn-block"
							onClick={this.addNewStaff}
						>
							{`+ ${lang.t("Add Staff")}`}
						</button>
					</div>
				</div>
				<div
					id="staff-list-header"
					className="row hidden-xs"
				>
					<div className="col-sm-1">{lang.t("ID")}</div>
					<div className="col-sm-2">{lang.t("Name")}</div>
					<div className="col-sm-2">{lang.t("Role")}</div>
					<div className="col-sm-3 col-md-2">{lang.t("Phone")}</div>
					<div className="col-sm-2 col-md-2">{lang.t("EMail")}</div>
				</div>
				<div
					id="staff-list-body"
					className="container"
				>
					<For
						each="member"
						of={staff}
						index="index"
					>
						<div
							className="row v-align-sm"
							role="button"
							key={`staff-member-${member.Id}`}
							tabIndex={0}
							onClick={this.onRowClick(member.Id)}
							onKeyPress={this.onRowKeyPress(member.Id)}
						>
							<div className="col-sm-1">{member.Id}</div>
							<div className="col-sm-2">
								{`${member.FirstName} ${member.LastName}`}
							</div>
							<div className="col-sm-2">
								<Choose>
									<When condition={member.IsViewOnly}>{lang.t("View Only")}</When>
									<When condition={member.IsAdmin}>
										{lang.t("Administrator")}
									</When>
									<When condition={member.IsCoordinator}>
										{lang.t("Coordinator")}
									</When>
									<Otherwise>{lang.t("Staff")}</Otherwise>
								</Choose>
							</div>
							<div className="col-sm-2">
								<a
									href={`tel:${utils.formatPhoneNumber(member.MobilePhone)}`}
									data-prevent-navigation
								>
									{utils.formatPhoneNumber(member.MobilePhone)}
								</a>
							</div>
							<div
								className="col-sm-3 truncate"
								title={member.EmailAddress}
							>
								<a
									href={`mailto:${member.EmailAddress}`}
									data-prevent-navigation
								>
									{member.EmailAddress}
								</a>
							</div>
							<div className="col-sm-2">
								<button
									type="button"
									id={`delete-staff-${member.Id}`}
									className={`btn btn-primary btn-block${member.Id === ContactId ? " hidden" : ""}`}
									onClick={this.openDeleteConfirmationModal(member.Id)}
									data-prevent-navigation
								>
									{lang.t("Delete")}
								</button>
							</div>
						</div>
					</For>
					<If condition={hasMore}>
						<ScrollDetect
							className="text-center"
							onVisible={this.getStaff}
						>
							<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
						</ScrollDetect>
					</If>
				</div>
				<ConfirmationModal ref={this._confirmationModal} />
				<AddContactModal
					showModal={showAddContactModal}
					closeModal={this.closeAddContactModal}
					onSaveNewStaff={this.onSaveNewStaff}
					contactId={ContactId}
					user={user}
					isFromStaff
					contact={staffMember}
					editMode={editMode}
					organizationId={user.Organization.Id}
				/>
				<ReassignJobsModal
					user={user}
					showReassignJobsModal={showReassignJobsModal}
					staffId={staffId}
					closeModal={this.closeReassignJobsModal}
					staff={staff}
				/>
			</div>
		);
	}
}

Staff.propTypes = {
	user: PropTypes.object,
};

Staff.defaultProps = {
	user: {},
};

export default Staff;
